import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import HelpTip from '../components/HelpTip';
import JsonEditor from '../components/JsonEditor';
import CsvTableEditor from '../components/CsvTableEditor';
import { ORG_FIELD_HELP, fieldHelp } from '../config/orgFieldHelp';

// ── Styles ─────────────────────────────────────────────────────────────────
const s = {
  page: { padding: '24px', maxWidth: '960px' },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '24px', flexWrap: 'wrap', gap: '16px',
  },
  title: { margin: 0, fontSize: '22px', color: '#1a1a1a' },
  controls: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },
  select: {
    padding: '8px 12px', fontSize: '14px', borderRadius: '6px',
    border: '1px solid #dee2e6', minWidth: '180px',
  },
  newOrgBtn: {
    padding: '8px 18px', fontSize: '13px', fontWeight: 600, border: '2px dashed #1e3a5f',
    borderRadius: '6px', cursor: 'pointer', background: '#fff', color: '#1e3a5f',
  },
  primaryBtn: {
    padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#fff',
    background: '#1e3a5f', border: 'none', borderRadius: '8px', cursor: 'pointer',
  },
  disabledBtn: {
    padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#fff',
    background: '#adb5bd', border: 'none', borderRadius: '8px', cursor: 'not-allowed',
  },
  cancelBtn: {
    padding: '10px 24px', fontSize: '14px', fontWeight: 600, color: '#495057',
    background: '#e9ecef', border: 'none', borderRadius: '8px', cursor: 'pointer',
  },
  phase: {
    marginBottom: '28px', padding: '20px', background: '#f8f9fa',
    borderRadius: '12px', border: '1px solid #dee2e6',
  },
  phaseHeader: {
    display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px',
  },
  phaseBadge: {
    padding: '2px 10px', fontSize: '11px', fontWeight: 700, color: '#fff',
    background: '#1e3a5f', borderRadius: '12px', textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  phaseTitle: { margin: 0, fontSize: '16px', fontWeight: 600, color: '#1a1a1a' },
  phaseDesc: { fontSize: '13px', color: '#6c757d', marginBottom: '16px' },
  fieldRow: {
    display: 'grid', gridTemplateColumns: '200px 1fr', gap: '8px',
    alignItems: 'center', marginBottom: '10px',
  },
  fieldLabel: { fontSize: '13px', fontWeight: 600, color: '#495057' },
  fieldInput: {
    padding: '7px 10px', fontSize: '13px', borderRadius: '6px',
    border: '1px solid #dee2e6', background: '#fff', width: '100%',
    boxSizing: 'border-box',
  },
  readonlyInput: {
    padding: '7px 10px', fontSize: '13px', borderRadius: '6px',
    border: '1px solid #e9ecef', background: '#f1f3f5', width: '100%',
    boxSizing: 'border-box', color: '#868e96',
  },
  statusPill: (ok) => ({
    display: 'inline-block', padding: '2px 10px', fontSize: '11px',
    fontWeight: 600, borderRadius: '10px',
    background: ok ? '#d3f9d8' : '#ffe3e3', color: ok ? '#2b8a3e' : '#c92a2a',
  }),
  error: { color: '#dc3545', fontSize: '13px', marginBottom: '12px' },
  success: { color: '#2b8a3e', fontSize: '13px', marginBottom: '12px' },
};

// ── Field component ─────────────────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, disabled, type = 'text', help, tip }) {
  return (
    <div style={s.fieldRow}>
      <label style={s.fieldLabel}>{label}{tip && <HelpTip {...tip} />}</label>
      <div>
        {type === 'textarea' ? (
          <textarea
            style={{ ...s.fieldInput, minHeight: '60px', resize: 'vertical' }}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
          />
        ) : (
          <input
            style={disabled ? s.readonlyInput : s.fieldInput}
            type={type}
            value={value || ''}
            onChange={e => onChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
          />
        )}
        {help && <div style={{ fontSize: '11px', color: '#868e96', marginTop: '2px' }}>{help}</div>}
      </div>
    </div>
  );
}

function SelectField({ label, value, onChange, options, disabled, help, tip }) {
  return (
    <div style={s.fieldRow}>
      <label style={s.fieldLabel}>{label}{tip && <HelpTip {...tip} />}</label>
      <div>
        <select
          style={disabled ? s.readonlyInput : s.fieldInput}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          disabled={disabled}
        >
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
        {help && <div style={{ fontSize: '11px', color: '#868e96', marginTop: '2px' }}>{help}</div>}
      </div>
    </div>
  );
}

function ArrayField({ label, value, onChange, placeholder, disabled, help, tip }) {
  const arr = Array.isArray(value) ? value : [];
  const text = arr.join(', ');
  return (
    <Field
      label={label}
      value={text}
      onChange={v => onChange(v.split(',').map(s => s.trim()).filter(Boolean))}
      placeholder={placeholder}
      disabled={disabled}
      help={help || 'Comma-separated list'}
      tip={tip}
    />
  );
}

// ── Phase sections ──────────────────────────────────────────────────────────

function LogEntry({ entry }) {
  const [open, setOpen] = useState(false);
  const icon = entry.status === 'ok' ? '✓' : entry.status === 'error' ? '✗'
    : entry.status === 'dry_run' ? '○' : entry.status === 'manual' || entry.status === 'warning' ? '⚠' : '–';
  const color = entry.status === 'ok' ? '#51cf66' : entry.status === 'error' ? '#ff6b6b'
    : entry.status === 'dry_run' ? '#ffd43b' : entry.status === 'manual' || entry.status === 'warning' ? '#ff922b' : '#868e96';
  const hasContent = !!entry.content;
  return (
    <div>
      <div style={{ color, display: 'flex', alignItems: 'baseline', gap: '2px' }}>
        <span style={{ display: 'inline-block', width: '16px', flexShrink: 0 }}>{icon}</span>
        <span style={{ color: '#dee2e6', marginRight: '8px', flexShrink: 0 }}>{entry.step}</span>
        <span style={{ color }}>{entry.message}</span>
        {hasContent && (
          <span
            onClick={() => setOpen(v => !v)}
            style={{ marginLeft: '8px', cursor: 'pointer', color: '#868e96', fontSize: '10px', flexShrink: 0 }}
          >
            {open ? '▾ hide' : '▸ show content'}
          </span>
        )}
      </div>
      {hasContent && open && (
        <pre style={{
          margin: '4px 0 6px 18px', padding: '8px', background: '#2d2d2d',
          borderRadius: '4px', fontSize: '11px', color: '#ced4da',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '200px',
          overflowY: 'auto', border: '1px solid #495057',
        }}>
          {entry.content}
        </pre>
      )}
    </div>
  );
}

function Phase({ number, title, description, children, status }) {
  return (
    <div style={s.phase}>
      <div style={s.phaseHeader}>
        <span style={s.phaseBadge}>Phase {number}</span>
        <h3 style={s.phaseTitle}>{title}</h3>
        {status && <span style={s.statusPill(status === 'ok')}>{status === 'ok' ? 'Configured' : 'Not configured'}</span>}
      </div>
      {description && <p style={s.phaseDesc}>{description}</p>}
      {children}
    </div>
  );
}

// ── Default empty config ────────────────────────────────────────────────────

const EMPTY_CONFIG = {
  display_name: '',
  type: 'sales',
  database: '',
  company_code: null,
  user_id_base: null,
  tabs: [],
  opportunity_entity_types: [],
  pain_entity_types: [],
};

const ORG_TYPES = [
  { value: 'sales', label: 'Sales' },
  { value: 'agri', label: 'Agriculture' },
];

// Default entity types per org type
const DEFAULT_ENTITY_TYPES = {
  sales: {
    opportunity_entity_types: ['Customer Concerns', 'Roof Issues'],
    pain_entity_types: ['Roof Issues', 'Customer Concerns', 'Urgency', 'Financing'],
  },
  agri: {
    opportunity_entity_types: ['Crop Issues', 'Product Interest'],
    pain_entity_types: ['Crop Issues', 'Product Interest', 'Weather Concerns', 'Pricing'],
  },
};


// ── File descriptions ───────────────────────────────────────────────────────
const FILE_DESCRIPTIONS = {
  'entity_library.csv': 'Seed entity list (Entity_type, Entity_Name, Entity_alias)',
  'topics.json': 'Conversation topic tags for semantic classification',
  'regions.json': 'Territory / region definitions',
  'relationship_types.json': 'Entity relationship graph schema',
  'outcome_config.json': 'Keywords for outcome classification (optional)',
  'passwd.json': 'User ID → agent name mapping',
};

// ── File row styles ─────────────────────────────────────────────────────────
const fs = {
  row: {
    display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px',
    background: '#fff', borderRadius: '6px', border: '1px solid #e9ecef',
    marginBottom: '6px', fontSize: '13px',
  },
  name: { fontWeight: 600, color: '#1a1a1a', minWidth: '180px', fontFamily: 'monospace', fontSize: '12px' },
  desc: { flex: 1, color: '#6c757d', fontSize: '12px' },
  size: { color: '#868e96', fontSize: '11px', minWidth: '60px', textAlign: 'right' },
  btn: {
    padding: '4px 10px', fontSize: '11px', fontWeight: 600, border: '1px solid #dee2e6',
    borderRadius: '4px', cursor: 'pointer', background: '#fff', color: '#495057',
  },
  btnPrimary: {
    padding: '4px 10px', fontSize: '11px', fontWeight: 600, border: 'none',
    borderRadius: '4px', cursor: 'pointer', background: '#1e3a5f', color: '#fff',
  },
};

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── One config file, edited in the page ─────────────────────────────────────
// Replaces the download / edit in a text editor / upload loop. JSON files get a
// structured form built from their own shape; CSV gets a table. Both keep a raw
// text view, because a file that has stopped parsing can only be fixed as text —
// and that is exactly the moment somebody needs to edit it.
function OrgFileEditor({ orgKey, filename, onSaved, onCancel }) {
  const [state, setState] = useState({ loading: true });
  const [raw, setRaw] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const isJson = filename.toLowerCase().endsWith('.json');

  useEffect(() => {
    setState({ loading: true });
    axios.get(`/api/dachido/org-file-content/${orgKey}/${filename}`)
      .then(res => {
        setState({
          loading: false,
          json: res.data.json,
          text: res.data.text ?? (res.data.json !== undefined ? JSON.stringify(res.data.json, null, 2) : ''),
        });
        // A file that no longer parses can only be repaired as text.
        if (res.data.parse_error) {
          setRaw(true);
          setError(`This file is not valid JSON: ${res.data.parse_error}`);
        }
      })
      .catch(err => setState({ loading: false, error: err.response?.data?.error || err.message }));
  }, [orgKey, filename]);

  const save = () => {
    setSaving(true);
    setError(null);
    const body = raw || state.json === undefined ? { text: state.text } : { json: state.json };
    axios.post(`/api/dachido/org-file-content/${orgKey}/${filename}`, body)
      .then(() => { setDirty(false); onSaved(); })
      .catch(err => setError(err.response?.data?.error || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const box = {
    margin: '-2px 0 6px 0', padding: '12px 14px', background: '#fff',
    border: '1px solid #e9ecef', borderTop: 'none', borderRadius: '0 0 6px 6px',
  };

  if (state.loading) return <div style={{ ...box, fontSize: '12px', color: '#868e96' }}>Loading {filename}…</div>;
  if (state.error) return <div style={{ ...box, fontSize: '12px', color: '#c92a2a' }}>{state.error}</div>;

  return (
    <div style={box}>
      {error && (
        <div style={{ fontSize: '12px', color: '#c92a2a', marginBottom: '8px' }}>{error}</div>
      )}

      {raw ? (
        <textarea
          value={state.text}
          onChange={e => { setState(st => ({ ...st, text: e.target.value })); setDirty(true); }}
          spellCheck={false}
          style={{
            width: '100%', minHeight: '320px', fontFamily: 'monospace', fontSize: '12px',
            padding: '10px', borderRadius: '6px', border: '1px solid #dee2e6',
            background: '#f8f9fa', resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5,
          }}
        />
      ) : isJson ? (
        <JsonEditor
          value={state.json}
          onChange={next => {
            setState(st => ({ ...st, json: next, text: JSON.stringify(next, null, 2) }));
            setDirty(true);
          }}
        />
      ) : (
        <CsvTableEditor
          text={state.text}
          onChange={next => { setState(st => ({ ...st, text: next })); setDirty(true); }}
        />
      )}

      <div style={{ display: 'flex', gap: '8px', marginTop: '10px', alignItems: 'center' }}>
        <button
          style={dirty && !saving ? fs.btnPrimary : { ...fs.btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}
          onClick={save}
          disabled={!dirty || saving}
        >
          {saving ? 'Saving…' : `Save ${filename}`}
        </button>
        <button style={fs.btn} onClick={onCancel}>Cancel</button>
        <button style={{ ...fs.btn, marginLeft: 'auto' }} onClick={() => setRaw(r => !r)}>
          {raw ? 'Back to form' : 'Edit as text'}
        </button>
      </div>
    </div>
  );
}

function OrgFiles({ orgKey }) {
  const [editing, setEditing] = useState(null);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);
  const [passwdUsers, setPasswdUsers] = useState(null);
  const [passwdOpen, setPasswdOpen] = useState(false);
  const uploadRefs = useRef({});

  const fetchFiles = useCallback(() => {
    if (!orgKey) return;
    setLoading(true);
    axios.get(`/api/dachido/org-files/${orgKey}`)
      .then(res => {
        if (res.data.success) {
          setFiles(res.data.files || []);
          // Auto-fetch passwd.json contents if it exists
          const passwd = (res.data.files || []).find(f => f.name === 'passwd.json');
          if (passwd && passwd.exists) {
            axios.get(`/api/dachido/org-files/${orgKey}/passwd.json`, { responseType: 'json' })
              .then(r => setPasswdUsers(r.data?.users || []))
              .catch(() => setPasswdUsers(null));
          } else {
            setPasswdUsers(null);
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orgKey]);

  useEffect(() => { fetchFiles(); }, [fetchFiles]);

  const handleDownload = (filename) => {
    window.open(`/api/dachido/org-files/${orgKey}/${filename}`, '_blank');
  };

  const handleUpload = (filename) => {
    const ref = uploadRefs.current[filename];
    if (ref) ref.click();
  };

  const handleFileSelected = (filename, file, exists) => {
    if (exists) {
      if (!window.confirm(`"${filename}" already exists for this org. Replace it?`)) return;
    }
    const formData = new FormData();
    formData.append('file', file);
    setActionMsg({ type: 'info', text: `Uploading ${filename}…` });
    axios.post(`/api/dachido/org-files/${orgKey}/${filename}`, formData)
      .then(res => {
        if (res.data.success) {
          setActionMsg({ type: 'ok', text: `${filename} uploaded (${formatBytes(res.data.size)})` });
          fetchFiles();
        } else {
          setActionMsg({ type: 'err', text: res.data.error || 'Upload failed' });
        }
      })
      .catch(err => setActionMsg({ type: 'err', text: err.response?.data?.error || 'Upload failed' }));
  };

  if (loading && files.length === 0) {
    return <div style={{ fontSize: '13px', color: '#868e96', padding: '8px 0' }}>Loading file status…</div>;
  }

  return (
    <div>
      {actionMsg && (
        <div style={{
          fontSize: '12px', padding: '6px 12px', borderRadius: '6px', marginBottom: '10px',
          background: actionMsg.type === 'ok' ? '#d3f9d8' : actionMsg.type === 'err' ? '#ffe3e3' : '#e7f5ff',
          color: actionMsg.type === 'ok' ? '#2b8a3e' : actionMsg.type === 'err' ? '#c92a2a' : '#1971c2',
        }}>
          {actionMsg.text}
        </div>
      )}
      {files.map(f => (
        <React.Fragment key={f.name}>
          <div style={fs.row}>
            <span style={fs.name}>
              {f.name}
              <HelpTip {...(fieldHelp(f.name, 'files') || {})} />
            </span>
            <span style={fs.desc}>{FILE_DESCRIPTIONS[f.name] || ''}</span>
            <span style={fs.size}>{f.exists ? formatBytes(f.size) : ''}</span>
            <span style={s.statusPill(f.exists)}>
              {f.exists ? 'exists' : f.error ? 'error' : 'missing'}
            </span>
            {f.error && (
              <span style={{ fontSize: '11px', color: '#c92a2a' }} title={f.error}>
                {f.error.length > 60 ? f.error.slice(0, 60) + '…' : f.error}
              </span>
            )}

            {/* Edit in place — the whole point: no download/edit/upload round trip.
                Only for text formats; a spreadsheet or an image has no sensible
                in-browser representation and stays download/upload. */}
            {f.exists && f.editable && (
              <button
                style={fs.btn}
                onClick={() => setEditing(e => (e === f.name ? null : f.name))}
              >
                {editing === f.name ? '▾ Close' : '✎ Edit'}
              </button>
            )}

            {/* Download — only if file exists */}
            {f.exists && (
              <button style={fs.btn} onClick={() => handleDownload(f.name)} title="Download">
                ↓ Download
              </button>
            )}

            {/* View toggle for passwd.json */}
            {f.name === 'passwd.json' && f.exists && passwdUsers && (
              <button style={fs.btn} onClick={() => setPasswdOpen(v => !v)}>
                {passwdOpen ? '▾ Hide' : '▸ View'}
              </button>
            )}

            {/* Upload — not available for passwd.json (auto-created with the org) */}
            {!f.init_only && (
              <>
                <button style={fs.btnPrimary} onClick={() => handleUpload(f.name)}>
                  ↑ Upload
                </button>
                <input
                  type="file"
                  ref={el => { uploadRefs.current[f.name] = el; }}
                  style={{ display: 'none' }}
                  accept={'.' + f.name.split('.').pop()}
                  onChange={e => {
                    if (e.target.files[0]) handleFileSelected(f.name, e.target.files[0], f.exists);
                    e.target.value = '';
                  }}
                />
              </>
            )}
          </div>

          {/* Inline editor */}
          {editing === f.name && (
            <OrgFileEditor
              orgKey={orgKey}
              filename={f.name}
              onSaved={() => { setEditing(null); fetchFiles(); }}
              onCancel={() => setEditing(null)}
            />
          )}

          {/* passwd.json inline viewer */}
          {f.name === 'passwd.json' && passwdOpen && passwdUsers && (
            <div style={{
              margin: '-2px 0 6px 0', padding: '10px 14px', background: '#fff',
              border: '1px solid #e9ecef', borderTop: 'none', borderRadius: '0 0 6px 6px',
              fontSize: '12px',
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #dee2e6' }}>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: '#495057' }}>User ID</th>
                    <th style={{ textAlign: 'left', padding: '4px 8px', fontWeight: 600, color: '#495057' }}>Username</th>
                  </tr>
                </thead>
                <tbody>
                  {passwdUsers.map((u, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f1f3f5' }}>
                      <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{u.userId}</td>
                      <td style={{ padding: '4px 8px' }}>{u.username}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {passwdUsers.length === 0 && (
                <div style={{ color: '#868e96', padding: '4px 0' }}>No users defined</div>
              )}
              <div style={{ marginTop: '6px', color: '#868e96', fontSize: '11px' }}>
                {passwdUsers.length} user{passwdUsers.length !== 1 ? 's' : ''}
              </div>
            </div>
          )}
        </React.Fragment>
      ))}
      {files.length === 0 && !loading && (
        <div style={{ fontSize: '12px', color: '#868e96' }}>No file info available (Azure Storage may not be configured)</div>
      )}
    </div>
  );
}


// ── Pipeline config (org_config.json) component ────────────────────────────

const PIPELINE_CONFIG_FIELDS = [
  { key: 'db_name', label: 'Database Name', group: 'core' },
  { key: 'container', label: 'Container', group: 'core' },
  { key: 'customer_type', label: 'Customer Type', group: 'core',
    type: 'select', options: [
      { value: 'sales', label: 'Sales' },
      { value: 'agriculture', label: 'Agriculture' },
      { value: 'exit_interview', label: 'Exit Interview' },
    ] },
  { key: 'country', label: 'Country', group: 'core', help: 'Affects ZIP regex (US = 5-digit, IN = 6-digit)' },
  { key: 'enable_location_resolver', label: 'Location Resolver', group: 'core', type: 'bool', help: 'GPS → region lookup (US only)' },
  { key: 'call_center_system_user_id', label: 'Call Center System User ID', group: 'core', type: 'number' },
  { key: 'cron_mkdir', label: 'Cron Mkdir', group: 'core', type: 'array', help: 'Local dirs cron pre-creates' },

  { key: 'default_llm', label: 'Default LLM', group: 'llm' },
  { key: 'default_llm_provider', label: 'Default LLM Provider', group: 'llm' },
  { key: 'validator_llm', label: 'Validator LLM', group: 'llm' },
  { key: 'validator_llm_provider', label: 'Validator LLM Provider', group: 'llm' },

  { key: 'insights_llm_provider', label: 'Insights LLM Provider', group: 'insights', help: '"local" or "cloud"' },
  { key: 'insights_local_endpoint', label: 'Local Endpoint', group: 'insights' },
  { key: 'insights_local_model', label: 'Local Model', group: 'insights' },
  { key: 'insights_cloud_api_key', label: 'Cloud API Key', group: 'insights' },
  { key: 'insights_cloud_endpoint', label: 'Cloud Endpoint', group: 'insights' },
  { key: 'insights_cloud_deployment', label: 'Cloud Deployment', group: 'insights' },
  { key: 'insights_cloud_api_version', label: 'Cloud API Version', group: 'insights' },

  // Read by the pipeline's cron_state.py. Defaults here must match its defaults:
  // absent means enabled, every tick — so turning blob-driven cron on does not
  // change the schedule of any org that already runs.
  { key: 'cron_enabled', label: 'Pipeline Cron', group: 'cron', type: 'bool', defaultValue: true,
    help: 'Whether the scheduled pipeline picks this org up at all. Off means it never runs unless someone runs it by hand.' },
  { key: 'cron_interval_minutes', label: 'Minimum Gap Between Runs', group: 'cron', type: 'number', defaultValue: 0,
    help: 'Minutes to wait after the last run before running again. 0 runs on every 15-minute tick. 60 is roughly hourly, 1440 roughly daily.' },
];

const PIPELINE_GROUPS = [
  { key: 'core', label: 'Core' },
  { key: 'cron', label: 'Cron Schedule' },
  { key: 'llm', label: 'LLM Defaults' },
  { key: 'insights', label: 'Insights LLM' },
];

function OrgPipelineConfig({ orgKey }) {
  const [expanded, setExpanded] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [original, setOriginal] = useState(null);
  const [exists, setExists] = useState(false);
  const [readonlyFields, setReadonlyFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  useEffect(() => {
    if (!expanded || !orgKey) return;
    setLoading(true);
    axios.get(`/api/dachido/org-pipeline-config/${orgKey}`)
      .then(res => {
        if (res.data.exists) {
          setExists(true);
          setCfg(res.data.config);
          setOriginal(JSON.parse(JSON.stringify(res.data.config)));
          setReadonlyFields(res.data.readonly_fields || []);
        } else {
          setExists(false);
          setCfg(null);
          setOriginal(null);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [expanded, orgKey]);

  const dirty = cfg && original && JSON.stringify(cfg) !== JSON.stringify(original);

  const setField = (key, value) => {
    setCfg(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    setActionMsg({ type: 'info', text: 'Saving…' });
    axios.post(`/api/dachido/org-pipeline-config/${orgKey}`, { config: cfg })
      .then(res => {
        if (res.data.success) {
          setCfg(res.data.config);
          setOriginal(JSON.parse(JSON.stringify(res.data.config)));
          setActionMsg({ type: 'ok', text: 'org_config.json saved.' });
        } else {
          setActionMsg({ type: 'err', text: res.data.error });
        }
      })
      .catch(err => setActionMsg({ type: 'err', text: err.response?.data?.error || 'Save failed' }));
  };

  const handleDiscard = () => {
    setCfg(JSON.parse(JSON.stringify(original)));
    setActionMsg(null);
  };

  const isReadonly = (key) => readonlyFields.includes(key);

  return (
    <div>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
          padding: '10px 12px', background: '#fff', borderRadius: '6px',
          border: '1px solid #dee2e6', fontSize: '13px', fontWeight: 600, color: '#1a1a1a',
          userSelect: 'none', marginBottom: expanded ? 0 : '6px',
        }}
      >
        <span style={{ fontSize: '11px', color: '#868e96' }}>{expanded ? '▾' : '▸'}</span>
        org_config.json
        <span style={{ fontWeight: 400, color: '#868e96', fontSize: '12px', marginLeft: '4px' }}>
          — Pipeline config (db, LLM, country, folders)
        </span>
        <span style={{ marginLeft: 'auto', ...s.statusPill(exists) }}>
          {exists ? 'configured' : 'not configured'}
        </span>
      </div>

      {expanded && (
        <div style={{
          padding: '14px', background: '#fff', border: '1px solid #dee2e6',
          borderTop: 'none', borderRadius: '0 0 6px 6px', marginBottom: '6px',
        }}>
          {loading && <div style={{ fontSize: '12px', color: '#868e96' }}>Loading…</div>}

          {actionMsg && (
            <div style={{
              fontSize: '12px', padding: '6px 12px', borderRadius: '6px', marginBottom: '10px',
              background: actionMsg.type === 'ok' ? '#d3f9d8' : actionMsg.type === 'err' ? '#ffe3e3' : '#e7f5ff',
              color: actionMsg.type === 'ok' ? '#2b8a3e' : actionMsg.type === 'err' ? '#c92a2a' : '#1971c2',
            }}>
              {actionMsg.text}
            </div>
          )}

          {exists && cfg && PIPELINE_GROUPS.map(group => (
            <div key={group.key} style={{ marginBottom: '14px' }}>
              <div style={{
                fontSize: '11px', fontWeight: 700, color: '#868e96', textTransform: 'uppercase',
                letterSpacing: '0.05em', marginBottom: '8px', paddingBottom: '4px',
                borderBottom: '1px solid #f1f3f5',
              }}>
                {group.label}
              </div>
              {PIPELINE_CONFIG_FIELDS.filter(f => f.group === group.key).map(field => {
                // A key absent from org_config.json must display the value the
                // pipeline will actually use, not an empty/false-looking one —
                // otherwise cron_enabled reads "Disabled" here while the pipeline
                // treats it as on.
                const val = cfg[field.key] ?? field.defaultValue;
                const ro = isReadonly(field.key);

                if (field.type === 'select') {
                  return (
                    <SelectField
                      key={field.key}
                      label={field.label}
                      value={val ?? ''}
                      onChange={v => setField(field.key, v)}
                      options={field.options}
                      disabled={ro}
                      help={ro ? 'Read-only' : field.help}
                      tip={fieldHelp(field.key, 'pipeline')}
                    />
                  );
                }
                if (field.type === 'bool') {
                  return (
                    <SelectField
                      key={field.key}
                      label={field.label}
                      value={val === true ? 'true' : 'false'}
                      onChange={v => setField(field.key, v === 'true')}
                      options={[{ value: 'true', label: 'Enabled' }, { value: 'false', label: 'Disabled' }]}
                      disabled={ro}
                      help={ro ? 'Read-only' : field.help}
                      tip={fieldHelp(field.key, 'pipeline')}
                    />
                  );
                }
                if (field.type === 'array') {
                  return (
                    <ArrayField
                      key={field.key}
                      label={field.label}
                      value={val}
                      onChange={v => setField(field.key, v)}
                      disabled={ro}
                      help={ro ? 'Read-only' : field.help}
                      tip={fieldHelp(field.key, 'pipeline')}
                    />
                  );
                }
                if (field.type === 'number') {
                  return (
                    <Field
                      key={field.key}
                      label={field.label}
                      value={val ?? ''}
                      onChange={v => setField(field.key, v ? parseInt(v, 10) || 0 : 0)}
                      disabled={ro}
                      type="number"
                      help={ro ? 'Read-only' : field.help}
                      tip={fieldHelp(field.key, 'pipeline')}
                    />
                  );
                }
                return (
                  <Field
                    key={field.key}
                    label={field.label}
                    value={val ?? ''}
                    onChange={v => setField(field.key, v)}
                    disabled={ro}
                    help={ro ? 'Read-only' : field.help}
                    tip={fieldHelp(field.key, 'pipeline')}
                  />
                );
              })}
            </div>
          ))}

          {/* Everything else in the file.
              The groups above are a curated list of the settings people change
              often. They are not the whole file — org_config.json also carries
              per-org keys like cron_env and signal_sentiment_rollup that no form
              knew about, and needing one of those was what sent people back to
              download / edit / upload. Anything not claimed by a group above shows
              here, editable, so the form is never the reason to leave the page. */}
          {exists && cfg && (() => {
            const known = new Set(PIPELINE_CONFIG_FIELDS.map(f => f.key));
            const others = Object.keys(cfg).filter(k => !known.has(k));
            if (others.length === 0) return null;
            return (
              <div style={{ marginBottom: '14px' }}>
                <div style={{
                  fontSize: '11px', fontWeight: 700, color: '#6c757d', textTransform: 'uppercase',
                  letterSpacing: '0.5px', margin: '14px 0 6px',
                }}>
                  Other settings ({others.length})
                </div>
                <JsonEditor
                  value={Object.fromEntries(others.map(k => [k, cfg[k]]))}
                  onChange={next => {
                    // Merge back rather than replace: the curated fields above are
                    // not in `next` and must survive.
                    setCfg(prev => ({ ...prev, ...next }));
                    setActionMsg(null);
                  }}
                />
              </div>
            );
          })()}

          {exists && cfg && (
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                style={dirty ? fs.btnPrimary : { ...fs.btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}
                onClick={handleSave}
                disabled={!dirty}
              >
                Save org_config.json
              </button>
              {dirty && (
                <button style={fs.btn} onClick={handleDiscard}>
                  Discard changes
                </button>
              )}
            </div>
          )}

          {!exists && !loading && (
            <div style={{ fontSize: '12px', color: '#868e96' }}>
              org_config.json not found. Create the organization to generate it.
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ── Agent config component ──────────────────────────────────────────────────

// ── Cron status ─────────────────────────────────────────────────────────────
// Read-only on purpose. cron_enabled / cron_interval_minutes live in
// org_config.json, which OrgPipelineConfig above owns end-to-end — it loads the
// whole file and saves the whole file. A second editor posting only its own two
// keys would overwrite everything else in that file, so the settings are edited
// there and this panel shows what the pipeline is actually doing with them.
function formatRunTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
}

// ── Production switches ─────────────────────────────────────────────────────
// Both live in companies/whisper_orgs.json, the file Whisper and the production
// cron read to decide which orgs they run. Each change is saved straight away.
const PIPELINE_SWITCHES = [
  { key: 'transcribe', label: 'Transcribe Recordings', tip: ORG_FIELD_HELP.transcribe_recordings,
    off: 'Off — Whisper skips this org', on: 'On — Whisper transcribes new recordings' },
  { key: 'run_pipeline', label: 'Run Pipeline', tip: ORG_FIELD_HELP.run_pipeline,
    off: 'Off — the cron skips this org', on: 'On — the cron processes new transcripts' },
];

function OrgPipelineSwitches({ orgKey }) {
  const [switches, setSwitches] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!orgKey) return;
    setSwitches(null);
    setError(null);
    axios.get(`/api/dachido/org-pipeline-switches/${orgKey}`)
      .then(res => setSwitches(res.data.switches))
      .catch(err => setError(err.response?.data?.error || err.message));
  }, [orgKey]);

  const change = (sw, on) => {
    if (!window.confirm(`${on ? 'Turn on' : 'Turn off'} "${sw.label}" for ${orgKey}? `
      + 'This takes effect in production on the next run.')) return;
    setSaving(true);
    setError(null);
    axios.post(`/api/dachido/org-pipeline-switches/${orgKey}`, { [sw.key]: on })
      .then(res => setSwitches(res.data.switches))
      .catch(err => setError(err.response?.data?.error || err.message))
      .finally(() => setSaving(false));
  };

  return (
    <div>
      {PIPELINE_SWITCHES.map(sw => (
        <SelectField
          key={sw.key}
          label={sw.label}
          tip={sw.tip}
          value={switches ? String(switches[sw.key]) : ''}
          onChange={v => change(sw, v === 'true')}
          disabled={!switches || saving}
          options={[
            ...(switches ? [] : [{ value: '', label: 'Loading…' }]),
            { value: 'false', label: sw.off },
            { value: 'true', label: sw.on },
          ]}
        />
      ))}
      {switches && !switches.listed && (
        <div style={{ fontSize: '12px', color: '#868e96', padding: '4px 0 8px' }}>
          Not in whisper_orgs.json yet — turning a switch on adds it.
        </div>
      )}
      {error && <div style={s.error}>{error}</div>}
      <OrgCronStatus orgKey={orgKey} runPipeline={switches ? switches.run_pipeline : null} />
    </div>
  );
}

// runPipeline: the Run Pipeline switch, or null while it is still loading.
function OrgCronStatus({ orgKey, runPipeline }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!orgKey) return;
    setLoading(true);
    axios.get(`/api/dachido/org-cron/${orgKey}`)
      .then(res => setData(res.data))
      .catch(err => setData({ error: err.response?.data?.error || err.message }))
      .finally(() => setLoading(false));
  }, [orgKey]);

  if (loading) return <div style={{ fontSize: '12px', color: '#868e96', padding: '8px 0' }}>Loading cron status…</div>;
  if (!data) return null;

  const settings = data.settings || {};
  const runs = data.runs || [];
  const last = runs[0];
  const interval = settings.cron_interval_minutes;

  return (
    <div>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
        {runPipeline !== null && (
          <span style={s.statusPill(runPipeline)}>
            {runPipeline ? 'Pipeline on' : 'Pipeline off'}
          </span>
        )}
        <span style={s.statusPill(settings.cron_enabled)}>
          {settings.cron_enabled ? 'Cron enabled' : 'Cron disabled'}
        </span>
        <span style={{ ...fs.row, marginBottom: 0, padding: '4px 10px' }}>
          {interval > 0 ? `At most once every ${interval} min` : 'Runs on every 15-minute tick'}
        </span>
      </div>

      <div style={{ ...fs.row, display: 'block' }}>
        <div><strong>Last run:</strong> {formatRunTime(last?.started_at)}
          {last && last.exit_code !== null && last.exit_code !== undefined && (
            <span style={{ marginLeft: '8px', color: last.exit_code === 0 ? '#2b8a3e' : '#c92a2a' }}>
              {last.exit_code === 0 ? 'succeeded' : `failed (exit ${last.exit_code})`}
            </span>
          )}
          {last && !last.finished_at && <span style={{ marginLeft: '8px', color: '#e67700' }}>still running</span>}
        </div>
        <div style={{ marginTop: '4px' }}><strong>Next run:</strong>{' '}
          {runPipeline === false
            ? 'never — Run Pipeline is off'
            : settings.cron_enabled
              ? (data.next_run_at ? formatRunTime(data.next_run_at) : 'next tick')
              : 'never — cron is disabled'}
        </div>
        {last?.error_summary && (
          <div style={{ marginTop: '6px', color: '#c92a2a', fontSize: '12px', wordBreak: 'break-word' }}>
            {last.error_summary}
          </div>
        )}
      </div>

      {data.error && (
        <div style={{ padding: '10px', background: '#fff3bf', borderRadius: '6px',
                      fontSize: '12px', color: '#e67700', marginTop: '8px' }}>
          {data.error}
        </div>
      )}

      {runs.length > 1 && (
        <details style={{ marginTop: '8px' }}>
          <summary style={{ fontSize: '12px', color: '#495057', cursor: 'pointer' }}>
            Previous {runs.length - 1} runs
          </summary>
          <div style={{ marginTop: '6px' }}>
            {runs.slice(1).map(r => (
              <div key={r.id} style={{ ...fs.row, fontSize: '12px' }}>
                <span style={{ minWidth: '160px' }}>{formatRunTime(r.started_at)}</span>
                <span style={{ color: r.exit_code === 0 ? '#2b8a3e' : '#c92a2a' }}>
                  {r.exit_code === 0 ? 'ok' : `exit ${r.exit_code}`}
                </span>
                <span style={{ color: '#868e96' }}>{r.environment}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      <div style={{ fontSize: '11px', color: '#868e96', marginTop: '8px' }}>
        Change these settings under <strong>Cron Schedule</strong> in Pipeline Config (Phase 3).
      </div>
    </div>
  );
}

// ── One agent's row: its switches, and its prompt ───────────────────────────
// Prompts are edited as the text an author actually writes. Stored as JSON string
// fields, a prompt arrives full of \n and \" escapes — unreadable and easy to
// corrupt by hand, which is why the old raw-JSON box was unusable for this.
function AgentRow({ orgKey, name, cfg, onChange }) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const [promptDirty, setPromptDirty] = useState(false);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const relPath = `prompts/${name}.json`;

  const loadPrompt = () => {
    if (prompt !== null) return;
    setBusy(true);
    axios.get(`/api/dachido/org-agents/${orgKey}/file/${relPath}`)
      .then(res => setPrompt(res.data.json || {}))
      .catch(err => setMsg({ err: err.response?.data?.error || 'No prompt file for this agent' }))
      .finally(() => setBusy(false));
  };

  const savePrompt = () => {
    setBusy(true);
    setMsg(null);
    axios.post(`/api/dachido/org-agents/${orgKey}/file/${relPath}`, { json: prompt })
      .then(() => { setPromptDirty(false); setMsg({ ok: 'Prompt saved' }); })
      .catch(err => setMsg({ err: err.response?.data?.error || 'Save failed' }))
      .finally(() => setBusy(false));
  };

  const setPromptField = (key, value) => {
    setPrompt(p => ({ ...p, [key]: value }));
    setPromptDirty(true);
  };

  const enabled = cfg.enabled !== false;

  return (
    <div style={{ ...fs.row, display: 'block' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => onChange({ ...cfg, enabled: e.target.checked })}
          />
          <span style={{ ...fs.name, minWidth: 'auto' }}>{name}</span>
        </label>
        <span style={s.statusPill(enabled)}>{enabled ? 'On' : 'Off'}</span>

        <label style={{ fontSize: '11px', color: '#6c757d' }}>
          Threshold{' '}
          <input
            type="number" step="0.05" min="0" max="1"
            value={cfg.threshold ?? ''}
            onChange={e => onChange({ ...cfg, threshold: e.target.value === '' ? undefined : Number(e.target.value) })}
            style={{ width: '70px', padding: '2px 6px', fontSize: '11px',
                     border: '1px solid #dee2e6', borderRadius: '4px' }}
          />
        </label>
        <label style={{ fontSize: '11px', color: '#6c757d' }}>
          Max tokens{' '}
          <input
            type="number" min="0" step="256"
            value={cfg.max_tokens ?? ''}
            onChange={e => onChange({ ...cfg, max_tokens: e.target.value === '' ? undefined : Number(e.target.value) })}
            style={{ width: '80px', padding: '2px 6px', fontSize: '11px',
                     border: '1px solid #dee2e6', borderRadius: '4px' }}
          />
        </label>

        <button
          style={{ ...fs.btn, marginLeft: 'auto' }}
          onClick={() => { setOpen(o => !o); if (!open) loadPrompt(); }}
        >
          {open ? 'Hide prompt' : 'Edit prompt'}
        </button>
      </div>

      {cfg._comment && (
        <div style={{ fontSize: '11px', color: '#868e96', marginTop: '4px' }}>{cfg._comment}</div>
      )}

      {open && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #e9ecef' }}>
          {busy && <div style={{ fontSize: '12px', color: '#868e96' }}>Working…</div>}
          {prompt && (
            <>
              {['system', 'user_template'].map(key => (
                prompt[key] !== undefined && (
                  <div key={key} style={{ marginBottom: '10px' }}>
                    <div style={{ fontSize: '11px', fontWeight: 600, color: '#495057',
                                  textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '4px' }}>
                      {key === 'system' ? 'System prompt' : 'User template'}
                    </div>
                    <textarea
                      value={prompt[key]}
                      onChange={e => setPromptField(key, e.target.value)}
                      spellCheck={false}
                      style={{ width: '100%', minHeight: key === 'system' ? '180px' : '120px',
                               fontFamily: 'monospace', fontSize: '12px', padding: '10px',
                               borderRadius: '6px', border: '1px solid #dee2e6', background: '#fff',
                               resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5 }}
                    />
                  </div>
                )
              ))}
              <div style={{ fontSize: '11px', color: '#868e96', marginBottom: '8px' }}>
                Other keys in this prompt file ({Object.keys(prompt).filter(k => !['system', 'user_template'].includes(k)).join(', ') || 'none'}) are preserved on save.
              </div>
              <button
                style={promptDirty ? fs.btnPrimary : { ...fs.btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}
                onClick={savePrompt}
                disabled={!promptDirty || busy}
              >
                Save prompt
              </button>
            </>
          )}
          {msg?.err && <div style={{ fontSize: '12px', color: '#c92a2a', marginTop: '6px' }}>{msg.err}</div>}
          {msg?.ok && <div style={{ fontSize: '12px', color: '#2b8a3e', marginTop: '6px' }}>{msg.ok}</div>}
        </div>
      )}
    </div>
  );
}

function OrgAgents({ orgKey }) {
  const [expanded, setExpanded] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [files, setFiles] = useState([]);
  const [agents, setAgents] = useState(null);
  const [exists, setExists] = useState(false);
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [editorText, setEditorText] = useState('');
  const [dirty, setDirty] = useState(false);
  const [loading, setLoading] = useState(false);
  const [actionMsg, setActionMsg] = useState(null);

  // Fetch agents.json and available sources when expanded
  useEffect(() => {
    if (!expanded || !orgKey) return;
    setLoading(true);
    Promise.all([
      axios.get(`/api/dachido/org-agents/${orgKey}`),
      axios.get('/api/dachido/org-agents/sources'),
    ])
      .then(([agRes, srcRes]) => {
        setFiles(agRes.data.files || []);
        if (agRes.data.exists) {
          setExists(true);
          setAgents(agRes.data.agents);
          setEditorText(JSON.stringify(agRes.data.agents, null, 2));
        } else {
          setExists(false);
          setAgents(null);
          setEditorText('');
        }
        const srcs = (srcRes.data.sources || []).filter(s => s.org_key !== orgKey);
        setSources(srcs);
        if (srcs.length && !selectedSource) setSelectedSource(srcs[0].org_key);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [expanded, orgKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = () => {
    if (!selectedSource) return;
    if (exists && !window.confirm('This will overwrite the current agents.json. Continue?')) return;
    setActionMsg({ type: 'info', text: `Copying from ${selectedSource}…` });
    axios.post(`/api/dachido/org-agents/${orgKey}/copy/${selectedSource}`)
      .then(res => {
        if (res.data.success) {
          setExists(true);
          setAgents(res.data.agents);
          setEditorText(JSON.stringify(res.data.agents, null, 2));
          setDirty(false);
          setActionMsg({ type: 'ok', text: `Copied from ${res.data.copied_from}. Org references updated automatically.` });
        } else {
          setActionMsg({ type: 'err', text: res.data.error });
        }
      })
      .catch(err => setActionMsg({ type: 'err', text: err.response?.data?.error || 'Copy failed' }));
  };

  const handleEditorChange = (text) => {
    setEditorText(text);
    setDirty(true);
  };

  const handleSave = () => {
    let parsed;
    try {
      parsed = JSON.parse(editorText);
    } catch (e) {
      setActionMsg({ type: 'err', text: `Invalid JSON: ${e.message}` });
      return;
    }
    setActionMsg({ type: 'info', text: 'Saving…' });
    axios.post(`/api/dachido/org-agents/${orgKey}`, { agents: parsed })
      .then(res => {
        if (res.data.success) {
          setAgents(parsed);
          setDirty(false);
          setActionMsg({ type: 'ok', text: 'agents.json saved.' });
        } else {
          setActionMsg({ type: 'err', text: res.data.error });
        }
      })
      .catch(err => setActionMsg({ type: 'err', text: err.response?.data?.error || 'Save failed' }));
  };

  return (
    <div>
      <div
        onClick={() => setExpanded(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
          padding: '10px 12px', background: '#fff', borderRadius: '6px',
          border: '1px solid #dee2e6', fontSize: '13px', fontWeight: 600, color: '#1a1a1a',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: '11px', color: '#868e96' }}>{expanded ? '▾' : '▸'}</span>
        agents.json
        <span style={{ fontWeight: 400, color: '#868e96', fontSize: '12px', marginLeft: '4px' }}>
          — Agent registry &amp; LLM settings
        </span>
        <span style={{ marginLeft: 'auto', ...s.statusPill(exists) }}>
          {exists ? 'configured' : 'not configured'}
        </span>
      </div>

      {expanded && (
        <div style={{
          padding: '14px', background: '#fff', border: '1px solid #dee2e6',
          borderTop: 'none', borderRadius: '0 0 6px 6px', marginBottom: '8px',
        }}>
          {loading && <div style={{ fontSize: '12px', color: '#868e96' }}>Loading…</div>}

          {actionMsg && (
            <div style={{
              fontSize: '12px', padding: '6px 12px', borderRadius: '6px', marginBottom: '10px',
              background: actionMsg.type === 'ok' ? '#d3f9d8' : actionMsg.type === 'err' ? '#ffe3e3' : '#e7f5ff',
              color: actionMsg.type === 'ok' ? '#2b8a3e' : actionMsg.type === 'err' ? '#c92a2a' : '#1971c2',
            }}>
              {actionMsg.text}
            </div>
          )}

          {/* Copy from existing org */}
          {sources.length > 0 && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              marginBottom: exists ? '14px' : '10px',
              padding: '10px 12px', background: '#f8f9fa', borderRadius: '6px',
            }}>
              <span style={{ fontSize: '12px', fontWeight: 600, color: '#495057' }}>Copy from:</span>
              <select
                style={{ ...s.select, minWidth: '140px', fontSize: '12px', padding: '5px 8px' }}
                value={selectedSource}
                onChange={e => setSelectedSource(e.target.value)}
              >
                {sources.map(src => (
                  <option key={src.org_key} value={src.org_key}>{src.display_name}</option>
                ))}
              </select>
              <button style={fs.btnPrimary} onClick={handleCopy}>
                Copy
              </button>
              <span style={{ fontSize: '11px', color: '#868e96' }}>
                Copies agents.json, prompts and seed lists; org name and file paths are retargeted
              </span>
            </div>
          )}

          {/* Editor */}
          {exists && (
            <>
              {!showRaw && (
                <div>
                  {Object.entries(agents?.agents || {}).map(([name, cfg]) => (
                    <AgentRow
                      key={name}
                      orgKey={orgKey}
                      name={name}
                      cfg={cfg}
                      onChange={next => {
                        // Merge into the loaded file rather than rebuilding it — each
                        // agent carries bespoke keys this form does not show, and
                        // rebuilding would silently drop them.
                        const updated = { ...agents, agents: { ...agents.agents, [name]: next } };
                        setAgents(updated);
                        setEditorText(JSON.stringify(updated, null, 2));
                        setDirty(true);
                      }}
                    />
                  ))}
                  {Object.keys(agents?.agents || {}).length === 0 && (
                    <div style={{ fontSize: '12px', color: '#868e96', padding: '6px 0' }}>
                      agents.json has no <code>agents</code> block.
                    </div>
                  )}
                </div>
              )}

              {showRaw && (
                <textarea
                  value={editorText}
                  onChange={e => handleEditorChange(e.target.value)}
                  spellCheck={false}
                  style={{
                    width: '100%', minHeight: '300px', fontFamily: 'monospace', fontSize: '12px',
                    padding: '10px', borderRadius: '6px', border: '1px solid #dee2e6',
                    background: '#f8f9fa', resize: 'vertical', boxSizing: 'border-box',
                    lineHeight: 1.5,
                  }}
                />
              )}

              <div style={{ display: 'flex', gap: '8px', marginTop: '8px', alignItems: 'center' }}>
                <button
                  style={dirty ? fs.btnPrimary : { ...fs.btnPrimary, opacity: 0.5, cursor: 'not-allowed' }}
                  onClick={handleSave}
                  disabled={!dirty}
                >
                  Save agents.json
                </button>
                {dirty && (
                  <button
                    style={fs.btn}
                    onClick={() => {
                      setEditorText(JSON.stringify(agents, null, 2));
                      setDirty(false);
                    }}
                  >
                    Discard changes
                  </button>
                )}
                <button style={{ ...fs.btn, marginLeft: 'auto' }} onClick={() => setShowRaw(r => !r)}>
                  {showRaw ? 'Back to agent list' : 'Edit raw JSON'}
                </button>
              </div>

              {files.length > 0 && (
                <details style={{ marginTop: '10px' }}>
                  <summary style={{ fontSize: '12px', color: '#495057', cursor: 'pointer' }}>
                    {files.length} file{files.length === 1 ? '' : 's'} in this org's agent bundle
                  </summary>
                  <div style={{ marginTop: '6px', fontSize: '11px', color: '#6c757d',
                                fontFamily: 'monospace', lineHeight: 1.7 }}>
                    {files.map(f => <div key={f}>{f}</div>)}
                  </div>
                </details>
              )}
            </>
          )}

          {!exists && !loading && sources.length === 0 && (
            <div style={{ fontSize: '12px', color: '#868e96', padding: '4px 0' }}>
              No agent sources available. Configure agents for at least one org first.
            </div>
          )}
        </div>
      )}

      <div style={{ padding: '8px 0 0 0', fontSize: '12px', color: '#868e96' }}>
        Stored in blob at <code>{orgKey}/config/agents/</code>. The pipeline reads it from there,
        falling back to its built-in copy when an org has none.
      </div>
    </div>
  );
}


// ── Main component ──────────────────────────────────────────────────────────

function OrgConfig({ currentUser }) {
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [isNewMode, setIsNewMode] = useState(false);
  const [newOrgKey, setNewOrgKey] = useState('');
  const [config, setConfig] = useState({ ...EMPTY_CONFIG });
  const [originalConfig, setOriginalConfig] = useState(null);
  const [tabDefaults, setTabDefaults] = useState({});
  const [moduleRegistry, setModuleRegistry] = useState([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [provisionLog, setProvisionLog] = useState([]);
  // The existing org whose database structure a new org copies (review of #94:
  // the schema comes from a live database, not a file). A one-off creation choice,
  // so it is sent beside the config rather than saved into it.
  const [schemaSource, setSchemaSource] = useState('');
  const [schemaSources, setSchemaSources] = useState([]);
  // Track which fields the user has manually edited (for smart defaults)
  const [touched, setTouched] = useState({});

  // Compute next available user_id_base from existing orgs
  const nextUserIdBase = React.useMemo(() => {
    const bases = organizations
      .map(o => o.user_id_base)
      .filter(b => typeof b === 'number' && b > 0);
    if (bases.length === 0) return 830000;
    return Math.max(...bases) + 10000;
  }, [organizations]);

  // Fetch org list + tab_defaults/module_registry on mount
  useEffect(() => {
    axios.get('/api/config/organizations')
      .then(res => {
        const orgs = (res.data.data || []).filter(o => o.type !== 'admin');
        setOrganizations(orgs);
      })
      .catch(() => {});
    axios.get('/api/dachido/schema-sources')
      .then(res => setSchemaSources(res.data.sources || []))
      .catch(() => {});
    // Fetch tab_defaults and module_registry from any org detail or a known org
    axios.get('/api/dachido/org-detail/drroof')
      .then(res => {
        if (res.data.success) {
          setTabDefaults(res.data.tab_defaults || {});
          setModuleRegistry(res.data.module_registry || []);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch org detail when selection changes
  const fetchOrgDetail = useCallback((orgKey) => {
    if (!orgKey) return;
    setError(null);
    setSuccessMsg(null);
    axios.get(`/api/dachido/org-detail/${orgKey}`)
      .then(res => {
        if (res.data.success) {
          setConfig(res.data.config);
          setOriginalConfig(JSON.parse(JSON.stringify(res.data.config)));
          if (res.data.tab_defaults) setTabDefaults(res.data.tab_defaults);
          if (res.data.module_registry) setModuleRegistry(res.data.module_registry);
        }
      })
      .catch(err => setError(err.response?.data?.error || 'Failed to load org config'));
  }, []);

  useEffect(() => {
    if (selectedOrg && !isNewMode) {
      fetchOrgDetail(selectedOrg);
    }
  }, [selectedOrg, isNewMode, fetchOrgDetail]);

  const handleOrgChange = (orgKey) => {
    setSelectedOrg(orgKey);
    setIsNewMode(false);
    setNewOrgKey('');
    setTouched({});
    setSuccessMsg(null);
    setError(null);
    setProvisionLog([]);
  };

  const handleNewOrg = () => {
    setIsNewMode(true);
    setSelectedOrg('');
    setNewOrgKey('');
    setTouched({});
    const defaultType = 'sales';
    const entityDefaults = DEFAULT_ENTITY_TYPES[defaultType] || {};
    const defaultTabs = tabDefaults[defaultType] || [];
    setConfig({
      ...EMPTY_CONFIG,
      type: defaultType,
      user_id_base: nextUserIdBase,
      tabs: [...defaultTabs],
      opportunity_entity_types: [...(entityDefaults.opportunity_entity_types || [])],
      pain_entity_types: [...(entityDefaults.pain_entity_types || [])],
    });
    setOriginalConfig(null);
    setSuccessMsg(null);
    setError(null);
    setProvisionLog([]);
  };

  const handleCancel = () => {
    setIsNewMode(false);
    if (originalConfig) {
      setConfig(JSON.parse(JSON.stringify(originalConfig)));
    }
    setSuccessMsg(null);
    setError(null);
    setProvisionLog([]);
  };

  // Update a single config field, marking it as touched
  const setField = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    if (isNewMode) {
      // Mark as touched if value is non-empty; unmark if cleared
      const isEmpty = value === '' || value === null || value === undefined
        || (Array.isArray(value) && value.length === 0);
      setTouched(prev => ({ ...prev, [key]: !isEmpty }));
    }
  };

  const hasChanges = () => {
    if (isNewMode) return !!newOrgKey && !!config.display_name && !!config.type;
    return JSON.stringify(config) !== JSON.stringify(originalConfig);
  };

  const handleSave = () => {
    const orgKey = isNewMode ? newOrgKey.trim().toLowerCase().replace(/\s+/g, '_') : selectedOrg;
    if (!orgKey) {
      setError('Organization key is required');
      return;
    }
    if (isNewMode && config.database && !schemaSource) {
      setError('Choose which organization to copy the database structure from (Phase 6)');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMsg(null);
    setProvisionLog([]);

    // Auto-derive storage fields from org key
    const saveConfig = {
      ...config,
      container: orgKey,
      blob_company_id: orgKey,
      blob_prefixes: [`${orgKey}/`],
    };

    const dryParam = testMode ? '?dry_run=true' : '';
    axios.post(`/api/dachido/org-detail/${orgKey}${dryParam}`, {
      config: saveConfig,
      schema_source: isNewMode ? schemaSource : undefined,
    })
      .then(res => {
        if (res.data.success) {
          if (res.data.dry_run) {
            setSuccessMsg('Test mode — no changes were made. Review the log below.');
          } else {
            setSuccessMsg(res.data.created ? `Organization "${orgKey}" created successfully.` : 'Configuration saved.');
            setConfig(saveConfig);
            setOriginalConfig(JSON.parse(JSON.stringify(saveConfig)));
          }
          if (res.data.log && res.data.log.length > 0) {
            setProvisionLog(res.data.log);
          }
          if (res.data.created) {
            // Refresh org list and switch to the new org
            axios.get('/api/config/organizations')
              .then(r => {
                const orgs = (r.data.data || []).filter(o => o.type !== 'admin');
                setOrganizations(orgs);
              })
              .catch(() => {});
            setIsNewMode(false);
            setSelectedOrg(orgKey);
          }
        } else {
          setError(res.data.error || 'Save failed');
        }
      })
      .catch(err => setError(err.response?.data?.error || 'Save failed'))
      .finally(() => setSaving(false));
  };

  const handleDelete = () => {
    if (!selectedOrg || isNewMode) return;
    const displayName = organizations.find(o => o.name === selectedOrg)?.display_name || selectedOrg;

    // In test mode, skip confirmations — just show what would happen
    if (!testMode) {
      // First confirmation
      if (!window.confirm(
        `Delete organization "${displayName}"?\n\nThis will permanently remove:\n` +
        `• Azure Blob container\n` +
        `• PostgreSQL database\n` +
        `• passwd.json\n` +
        `• organizations.json entry\n\n` +
        `This action cannot be undone.`
      )) return;

      // Second confirmation — require typing the org key
      const typed = window.prompt(
        `To confirm deletion, type the organization key: ${selectedOrg}`
      );
      if (typed !== selectedOrg) {
        if (typed !== null) setError('Org key did not match. Deletion cancelled.');
        return;
      }
    }

    setDeleting(true);
    setError(null);
    setSuccessMsg(null);
    setProvisionLog([]);

    const dryParam = testMode ? '?dry_run=true' : '';
    axios.delete(`/api/dachido/org-detail/${selectedOrg}${dryParam}`)
      .then(res => {
        if (res.data.success) {
          if (res.data.dry_run) {
            setSuccessMsg('Test mode — no changes were made. Review the log below.');
          } else {
            setSuccessMsg(`Organization "${displayName}" deleted.`);
            // Refresh org list and clear selection
            axios.get('/api/config/organizations')
              .then(r => {
                const orgs = (r.data.data || []).filter(o => o.type !== 'admin');
                setOrganizations(orgs);
              })
              .catch(() => {});
            setSelectedOrg('');
            setConfig({ ...EMPTY_CONFIG });
            setOriginalConfig(null);
          }
          if (res.data.log) setProvisionLog(res.data.log);
        } else {
          setError(res.data.error || 'Delete failed');
        }
      })
      .catch(err => setError(err.response?.data?.error || 'Delete failed'))
      .finally(() => setDeleting(false));
  };

  // Derive available tabs from type + tab_defaults
  const orgType = config.type || 'sales';
  const defaultTabsForType = tabDefaults[orgType] || [];
  const allModules = moduleRegistry.length
    ? moduleRegistry.filter(m => !m.admin_only)
    : [];

  const effectiveOrgKey = isNewMode ? newOrgKey : selectedOrg;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <h2 style={s.title}>Org Config</h2>
        <div style={s.controls}>
          <button style={s.newOrgBtn} onClick={handleNewOrg}>
            + New Organization
          </button>
          <label style={{
            display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px',
            fontWeight: 600, color: testMode ? '#e67700' : '#868e96', cursor: 'pointer',
            padding: '6px 12px', borderRadius: '6px',
            background: testMode ? '#fff3bf' : 'transparent',
            border: testMode ? '1px solid #ffd43b' : '1px solid transparent',
          }}>
            <input
              type="checkbox"
              checked={testMode}
              onChange={e => setTestMode(e.target.checked)}
              style={{ cursor: 'pointer' }}
            />
            Test mode
          </label>
          {!isNewMode && (
            <>
              <label style={{ fontSize: '13px', fontWeight: 600, color: '#495057' }}>Organization:</label>
              <select
                style={s.select}
                value={selectedOrg}
                onChange={e => handleOrgChange(e.target.value)}
              >
                <option value="">— select —</option>
                {organizations.map(o => (
                  <option key={o.name} value={o.name}>{o.display_name}</option>
                ))}
              </select>
            </>
          )}
        </div>
      </div>

      {error && <p style={s.error}>{error}</p>}
      {successMsg && <p style={s.success}>{successMsg}</p>}

      {/* Provisioning log */}
      {provisionLog.length > 0 && (
        <div style={{
          marginBottom: '24px', padding: '16px', background: '#1a1a1a',
          borderRadius: '8px', fontFamily: 'monospace', fontSize: '12px',
          lineHeight: 1.8, maxHeight: '300px', overflowY: 'auto',
        }}>
          <div style={{ fontSize: '11px', fontWeight: 700, color: '#868e96', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: '8px' }}>
            Action Log
          </div>
          {provisionLog.map((entry, i) => (
            <LogEntry key={i} entry={entry} />
          ))}
          {(() => {
            const dryCount = provisionLog.filter(e => e.status === 'dry_run').length;
            const okCount = provisionLog.filter(e => e.status === 'ok').length;
            const errCount = provisionLog.filter(e => e.status === 'error').length;
            const manualCount = provisionLog.filter(e => e.status === 'manual').length;
            return (
              <div style={{ marginTop: '8px', fontSize: '11px', color: '#868e96' }}>
                {dryCount > 0
                  ? `Test mode: ${dryCount} actions would be executed`
                  : `${okCount}/${provisionLog.length - manualCount} steps completed${errCount ? `, ${errCount} error(s)` : ''}`}
                {manualCount > 0 && ` · ${manualCount} manual step(s) required`}
              </div>
            );
          })()}
        </div>
      )}

      {/* Only show phases when an org is selected or creating new */}
      {(isNewMode || selectedOrg) && (
        <>
          {/* ── Phase 1: Core Identity ────────────────────────────────────── */}
          <Phase
            number="1"
            title="Core Identity & Directory Config"
            description="Organization key, display name, type, and database binding."
            status={config.display_name && config.type ? 'ok' : 'missing'}
          >
            {isNewMode && (
              <Field
                label="Org Key"
                value={newOrgKey}
              tip={ORG_FIELD_HELP.org_key}
                onChange={v => {
                  const key = v.toLowerCase().replace(/\s+/g, '_');
                  setNewOrgKey(key);
                  // Auto-fill display_name if not touched
                  if (!touched.display_name) {
                    const displayName = key ? key.charAt(0).toUpperCase() + key.slice(1) : '';
                    setConfig(prev => ({ ...prev, display_name: displayName }));
                  }
                  // Auto-fill database if not touched
                  if (!touched.database) {
                    setConfig(prev => ({ ...prev, database: key }));
                  }
                }}
                placeholder="e.g. acme (lowercase, no spaces)"
                help="Unique identifier — used as directory name and config key. Cannot be changed later."
              />
            )}
            {!isNewMode && (
              <Field label="Org Key" value={selectedOrg} disabled />
            )}
            <Field
              label="Display Name"
              value={config.display_name}
              tip={ORG_FIELD_HELP.display_name}
              onChange={v => setField('display_name', v)}
              placeholder="e.g. Acme Corp"
            />
            <SelectField
              label="Type"
              value={config.type}
              tip={ORG_FIELD_HELP.type}
              onChange={v => {
                setField('type', v);
                if (isNewMode) {
                  // Auto-set tabs from defaults when type changes (unless user touched)
                  if (!touched.tabs) {
                    const defTabs = tabDefaults[v] || [];
                    setConfig(prev => ({ ...prev, tabs: [...defTabs] }));
                  }
                  // Auto-set entity types (unless user touched)
                  const entityDefaults = DEFAULT_ENTITY_TYPES[v] || {};
                  if (!touched.opportunity_entity_types) {
                    setConfig(prev => ({ ...prev, opportunity_entity_types: [...(entityDefaults.opportunity_entity_types || [])] }));
                  }
                  if (!touched.pain_entity_types) {
                    setConfig(prev => ({ ...prev, pain_entity_types: [...(entityDefaults.pain_entity_types || [])] }));
                  }
                }
              }}
              options={ORG_TYPES}
              help="Determines which dashboard tabs are shown by default (see Phase 4 below)"
            />
            <Field
              label="Database"
              value={config.database}
              tip={ORG_FIELD_HELP.database}
              onChange={v => setField('database', v)}
              placeholder="e.g. acme"
              help="PostgreSQL database name for this org's data"
            />
            <Field
              label="Company Code"
              value={config.company_code ?? ''}
              tip={ORG_FIELD_HELP.company_code}
              onChange={v => setField('company_code', v ? parseInt(v, 10) || null : null)}
              placeholder="e.g. 7007"
              type="number"
              help="Numeric company identifier (optional)"
            />
          </Phase>

          {/* ── Phase 2: Blob Storage & Data Paths ────────────────────────── */}
          <Phase
            number="2"
            title="Storage & Data Paths"
            description="Azure Blob container and user ID range. Container, blob company ID, and blob prefixes are derived automatically from the org key."
            status={effectiveOrgKey ? 'ok' : 'missing'}
          >
            <Field
              label="Container"
              value={effectiveOrgKey || '(set org key above)'}
              disabled
              help="Auto-derived from org key"
              tip={ORG_FIELD_HELP.container}
            />
            <Field
              label="Blob Prefix"
              value={effectiveOrgKey ? `${effectiveOrgKey}/` : '(set org key above)'}
              disabled
              help="Auto-derived from org key"
            />
            <Field
              label="User ID Base"
              value={config.user_id_base ?? ''}
              tip={ORG_FIELD_HELP.user_id_base}
              onChange={v => setField('user_id_base', v ? parseInt(v, 10) || null : null)}
              placeholder="e.g. 830000"
              type="number"
              help="Base for synthetic user IDs (10k range per org)"
            />
          </Phase>

          {/* ── Phase 3: Entity & Domain Config ───────────────────────────── */}
          <Phase
            number="3"
            title="Entity & Domain Configuration"
            description="Entity types for dashboard scoring, plus pipeline config files stored in Azure Blob Storage."
            status="ok"
          >
            <ArrayField
              label="Opportunity Entity Types"
              value={config.opportunity_entity_types}
              tip={ORG_FIELD_HELP.opportunity_entity_types}
              onChange={v => setField('opportunity_entity_types', v)}
              placeholder="e.g. Customer Concerns, Roof Issues"
              help="Entity types used for opportunity scoring in Sales Strategy"
            />
            <ArrayField
              label="Pain Entity Types"
              value={config.pain_entity_types}
              tip={ORG_FIELD_HELP.pain_entity_types}
              onChange={v => setField('pain_entity_types', v)}
              placeholder="e.g. Roof Issues, Customer Concerns"
              help="Entity types used for pain signal detection"
            />

            <h4 style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a', margin: '20px 0 10px' }}>
              Pipeline Config &amp; Files
            </h4>
            {!isNewMode && effectiveOrgKey ? (
              <>
                <OrgPipelineConfig orgKey={effectiveOrgKey} />
                <OrgFiles orgKey={effectiveOrgKey} />
              </>
            ) : (
              <div style={{ fontSize: '12px', color: '#868e96', padding: '8px 0' }}>
                Save the organization first to manage pipeline files.
              </div>
            )}
          </Phase>

          {/* ── Phase 4: Dashboard Tabs ────────────────────────────────────── */}
          <Phase
            number="4"
            title="Dashboard Tabs"
            description="Which dashboard pages are visible for this organization."
            status={config.tabs && config.tabs.length > 0 ? 'ok' : 'missing'}
          >
            {defaultTabsForType.length > 0 && (
              <div style={{ fontSize: '12px', color: '#868e96', marginBottom: '12px' }}>
                Default tabs for "{orgType}" orgs: {defaultTabsForType.join(', ')}
              </div>
            )}
            <ArrayField
              label="Tabs"
              value={config.tabs}
              onChange={v => setField('tabs', v)}
              placeholder="e.g. sales-home, sales-intel"
              help="Comma-separated module IDs. Leave empty to use type defaults."
            />
            {allModules.length > 0 && (
              <details style={{ marginTop: '8px', fontSize: '12px', color: '#495057' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Available modules</summary>
                <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {allModules.map(m => (
                    <span key={m.id} style={{
                      padding: '2px 8px', background: '#e9ecef', borderRadius: '4px',
                      fontSize: '11px', cursor: 'pointer',
                    }}
                      onClick={() => {
                        const tabs = config.tabs || [];
                        if (!tabs.includes(m.id)) setField('tabs', [...tabs, m.id]);
                      }}
                      title={`Click to add "${m.id}"`}
                    >
                      {m.id} ({m.label})
                    </span>
                  ))}
                </div>
              </details>
            )}
            <div style={{ marginTop: '12px' }}>
              <SelectField
                label="No Shared Admin Tabs"
                value={config.no_shared_admin_tabs ? 'yes' : 'no'}
                onChange={v => setField('no_shared_admin_tabs', v === 'yes')}
                options={[
                  { value: 'no', label: 'No — include shared admin tabs (FieldHQ, Custom)' },
                  { value: 'yes', label: 'Yes — do not include shared admin tabs' },
                ]}
                help="Controls whether shared admin tabs are appended for admin users"
              />
            </div>
          </Phase>

          {/* ── Phase 5: AI Agent Config ───────────────────────────────────── */}
          <Phase
            number="5"
            title="AI Agent Configuration"
            description="LLM agents for quality gating, sentiment scoring, and domain extraction."
          >
            {!isNewMode && effectiveOrgKey ? (
              <OrgAgents orgKey={effectiveOrgKey} />
            ) : (
              <div style={{ fontSize: '12px', color: '#868e96', padding: '8px 0' }}>
                Save the organization first to configure agents.
              </div>
            )}
          </Phase>

          {/* ── Phase 6: Database Setup ────────────────────────────────────── */}
          <Phase
            number="6"
            title="Database Setup"
            description="PostgreSQL database, created with the same structure as an existing organization's database."
          >
            <Field
              label="Production DB"
              value={config.database}
              tip={ORG_FIELD_HELP.database}
              disabled
              help="Set in Phase 1 above"
            />
            {isNewMode && (
              <SelectField
                label="Copy Structure From"
                value={schemaSource}
                onChange={setSchemaSource}
                options={[
                  { value: '', label: '— choose an organization —' },
                  ...schemaSources.map(src => ({ value: src.org_key, label: `${src.display_name} (${src.database})` })),
                ]}
                tip={ORG_FIELD_HELP.schema_source}
                help="Tables, views and fixed lookup rows are copied from its live database. No customer data is copied."
              />
            )}
            <div style={{ padding: '12px', background: '#e7f5ff', borderRadius: '8px', fontSize: '12px', color: '#1971c2', marginTop: '8px' }}>
              {isNewMode
                ? 'The database is created, and its structure copied, when the organization is saved.'
                : 'This database got its structure when the organization was created.'}
            </div>
          </Phase>

          {/* ── Phase 7: Production & Cron ─────────────────────────────────── */}
          <Phase
            number="7"
            title="Production & Cron Setup"
            description="Deployment, environment variables, and cron job."
          >
            {!isNewMode && effectiveOrgKey ? (
              <OrgPipelineSwitches orgKey={effectiveOrgKey} />
            ) : (
              <div style={{ fontSize: '12px', color: '#868e96', padding: '8px 0' }}>
                Save the organization first to set its production switches and see its cron status.
              </div>
            )}
            <div style={{ padding: '12px', background: '#fff3bf', borderRadius: '8px', fontSize: '12px', color: '#e67700', marginTop: '8px' }}>
              <strong>Still manual</strong> — update in the pipeline repo:<br />
              • <code>run_pipeline.py</code> → <code>_org_team_names</code> dict: add <code>'{effectiveOrgKey}': 'Display Name'</code>
            </div>
          </Phase>

          {/* ── Phase 8: Validation ────────────────────────────────────────── */}
          <Phase
            number="8"
            title="Production Validation"
            description="Post-deployment checks to confirm the pipeline is processing correctly."
          >
            <div style={{ padding: '12px', background: '#e7f5ff', borderRadius: '8px', fontSize: '12px', color: '#1971c2' }}>
              <strong>Validation checklist:</strong>
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '18px', lineHeight: 1.8 }}>
                <li><code>fact_conversation</code> row count matches transcript count</li>
                <li>No <code>NULL agent_id</code> rows (check passwd.json)</li>
                <li><code>overall_sentiment</code> is not uniformly 0.5 (LLM ran)</li>
                <li>Aggregation tables populated (<code>agg_entity_frequency</code>, etc.)</li>
                <li>Review queue items accessible in staging dashboard</li>
              </ul>
              Use <code>onboard_org.py --steps G</code> to run a validation pipeline with test transcripts.
            </div>
          </Phase>

          {/* ── Action buttons ────────────────────────────────────────────── */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '8px', alignItems: 'center' }}>
            <button
              style={hasChanges() && !saving ? s.primaryBtn : s.disabledBtn}
              onClick={handleSave}
              disabled={!hasChanges() || saving}
            >
              {saving ? 'Saving…' : isNewMode ? 'Create Organization' : 'Save Changes'}
            </button>
            {(isNewMode || hasChanges()) && (
              <button style={s.cancelBtn} onClick={handleCancel}>
                Cancel
              </button>
            )}
            {/* Delete — only for existing orgs, pushed to the right */}
            {!isNewMode && selectedOrg && (
              <button
                style={{
                  marginLeft: 'auto', padding: '10px 24px', fontSize: '14px', fontWeight: 600,
                  color: '#fff', background: deleting ? '#adb5bd' : '#c92a2a', border: 'none',
                  borderRadius: '8px', cursor: deleting ? 'not-allowed' : 'pointer',
                }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete Organization'}
              </button>
            )}
          </div>
        </>
      )}

      {/* Empty state */}
      {!isNewMode && !selectedOrg && (
        <div style={{ textAlign: 'center', padding: '60px 20px', color: '#868e96' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚙️</div>
          <p style={{ fontSize: '16px', fontWeight: 600 }}>Select an organization or create a new one</p>
          <p style={{ fontSize: '13px' }}>View and edit organization configuration following the onboarding checklist.</p>
        </div>
      )}
    </div>
  );
}

export default OrgConfig;
